import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from "@angular/common/http";
import { Observable } from "rxjs";
import { apiEnvironment } from "@kells/clinic-one/environments";

import { SSOService } from "../services";
import { HALEON_APP_HEADER } from "../../haleon/constants";

/**
 * SSO Auth Interceptor
 * Automatically adds SSO authentication tokens to API requests
 * Works alongside existing Auth0 interceptor for unified auth
 */
@Injectable()
export class SSOAuthInterceptor implements HttpInterceptor {
  constructor(private ssoService: SSOService) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Only add SSO tokens to our API requests
    if (!this.shouldAddSSOToken(request)) {
      return next.handle(request);
    }

    // Get stored SSO tokens
    const ssoTokens = this.ssoService.getStoredTokens();

    // If we have valid SSO tokens, add them to the request
    if (ssoTokens && ssoTokens.access_token) {
      const authRequest = request.clone({
        setHeaders: {
          Authorization: `Bearer ${ssoTokens.access_token}`,
        },
      });

      return next.handle(authRequest);
    }

    // If no SSO tokens, continue without modification
    return next.handle(request);
  }

  /**
   * Determine if SSO token should be added to this request
   * @param url - Request URL
   * @returns boolean indicating whether to add SSO token
   */
  private shouldAddSSOToken(request: HttpRequest<unknown>): boolean {
    // Only add to our API requests
    const isApiRequest = request.url.startsWith(apiEnvironment.apiBaseUrl);

    // Don't add to SSO auth endpoint (would cause circular requests)
    const isSSOAuthEndpoint = request.url.includes("/auth/sso/");
    const isHalenonApp = request.headers.has(HALEON_APP_HEADER);

    return isApiRequest && !isSSOAuthEndpoint && !isHalenonApp;
  }
}
